import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaSearch,
  FaSyncAlt,
  FaMicrochip,
  FaEdit,
  FaSave,
  FaTimes,
  FaUser,
  FaCalendarAlt,
} from "react-icons/fa";

import { request } from "../../services/api";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import styles from "../Monitor/Monitor.module.css";

const AdminDevices = () => {
  const navigate = useNavigate();
  const ownerDropdownRef = useRef(null);
  const isMobile = useIsMobile();

  const [search, setSearch] = useState("");
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editDevice, setEditDevice] = useState(null);
  const [editForm, setEditForm] = useState({
    device_serial: "",
    device_name: "",
    service_number: "",
    owner_id: "",
    owner_label: "",
    start_date: "",
    due_date: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  const [ownerSearch, setOwnerSearch] = useState("");
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [ownerUsersLoading, setOwnerUsersLoading] = useState(false);
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);

  const loadDevices = async (searchText = search) => {
    setLoading(true);
    setError("");

    try {
      const json = await request(
        `/admin_get_devices.php?search=${encodeURIComponent(searchText.trim())}&limit=100`,
      );

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت دستگاه‌ها");
      }

      setDevices(json.devices || []);
    } catch (err) {
      console.error("Admin devices load error:", err);
      setError(err.message || "خطا در دریافت دستگاه‌ها");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDevices(search);
    }, 350);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const searchOwners = async (text = ownerSearch) => {
    setOwnerUsersLoading(true);

    try {
      const json = await request(
        `/admin_get_users.php?search=${encodeURIComponent(text.trim())}&limit=50`,
      );

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در دریافت کاربران");
      }

      setOwnerUsers(json.users || []);
    } catch (err) {
      console.error("Search owners error:", err);
      setOwnerUsers([]);
      setError(err.message || "خطا در دریافت کاربران");
    } finally {
      setOwnerUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!editDevice) return;
    if (!isOwnerDropdownOpen) return;

    const timer = setTimeout(() => {
      searchOwners(ownerSearch);
    }, 350);

    return () => clearTimeout(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerSearch, isOwnerDropdownOpen, editDevice]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        ownerDropdownRef.current &&
        !ownerDropdownRef.current.contains(event.target)
      ) {
        setIsOwnerDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openEditModal = (device) => {
    const ownerLabel = buildOwnerLabel(device);

    setEditDevice(device);
    setEditForm({
      device_serial: device.device_serial || "",
      device_name: device.device_name || "",
      service_number: device.service_number || "",
      owner_id: device.owner_id || "",
      owner_label: ownerLabel,
      start_date: device.start_date || "",
      due_date: device.due_date || "",
    });

    setOwnerSearch(ownerLabel);
    setOwnerUsers([]);
    setIsOwnerDropdownOpen(false);
    setError("");
  };

  const closeEditModal = () => {
    setEditDevice(null);
    setEditForm({
      device_serial: "",
      device_name: "",
      service_number: "",
      owner_id: "",
      owner_label: "",
      start_date: "",
      due_date: "",
    });

    setOwnerSearch("");
    setOwnerUsers([]);
    setIsOwnerDropdownOpen(false);
    setEditLoading(false);
  };

  const submitEditDevice = async (e) => {
    e.preventDefault();

    if (!editDevice) return;

    if (!editForm.device_serial.trim()) {
      setError("سریال دستگاه الزامی است");
      return;
    }

    if (!editForm.service_number.trim()) {
      setError("شماره سرویس الزامی است");
      return;
    }

    if (!editForm.owner_id) {
      setError("مالک دستگاه الزامی است");
      return;
    }

    setEditLoading(true);
    setError("");

    try {
      const json = await request("/admin_update_device.php", "POST", {
        device_id: editDevice.id,
        device_serial: editForm.device_serial.trim(),
        device_name: editForm.device_name.trim(),
        service_number: editForm.service_number.trim(),
        owner_id: Number(editForm.owner_id),
        start_date: editForm.start_date || "",
        due_date: editForm.due_date || "",
      });

      if (!json || json.status !== "success") {
        throw new Error(json?.message || "خطا در ویرایش دستگاه");
      }

      setDevices((prev) =>
        prev.map((item) =>
          item.id === editDevice.id ? { ...item, ...json.device } : item,
        ),
      );

      closeEditModal();
    } catch (err) {
      console.error("Update device error:", err);
      setError(err.message || "خطا در ویرایش دستگاه");
    } finally {
      setEditLoading(false);
    }
  };

  const handleOwnerInputFocus = () => {
    setIsOwnerDropdownOpen(true);

    if (ownerUsers.length === 0) {
      searchOwners(ownerSearch);
    }
  };

  const handleSelectOwner = (user) => {
    const label =
      `${user.name || ""} ${user.family || ""} - ${user.phone_num1 || user.username || ""}`.trim();

    setEditForm((prev) => ({
      ...prev,
      owner_id: user.id,
      owner_label: label,
    }));

    setOwnerSearch(label);
    setIsOwnerDropdownOpen(false);
    setError("");
  };

  const clearOwner = () => {
    setEditForm((prev) => ({
      ...prev,
      owner_id: "",
      owner_label: "",
    }));

    setOwnerSearch("");
    setOwnerUsers([]);
    setIsOwnerDropdownOpen(true);

    setTimeout(() => {
      searchOwners("");
    }, 0);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const renderDeviceMobileCard = (device) => (
    <div key={device.id} style={mobileDeviceCardStyle}>
      <div style={mobileDeviceCardHeaderStyle}>
        <div>
          <div style={mobileCardTitleStyle}>
            {device.device_name || "بدون نام"}
          </div>
          <code style={mobileServiceCodeStyle}>
            {device.service_number || "-"}
          </code>
        </div>

        <button
          type="button"
          onClick={() => openEditModal(device)}
          style={getMobileEditButtonStyle(editButtonStyle)}
        >
          <FaEdit />
          ویرایش
        </button>
      </div>

      <div style={mobileMetaGridStyle}>
        <InfoRow label="سریال" value={device.device_serial} />
        <InfoRow label="مالک" value={buildOwnerName(device) || "بدون نام"} />
        <InfoRow label="موبایل مالک" value={device.owner_phone || "-"} />
        <InfoRow label="شروع" value={formatDate(device.start_date)} />
        <InfoRow label="پایان" value={formatDate(device.due_date)} />
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.deviceTitle}>
            <span style={{ fontSize: "2rem" }}>🔌</span>
            <div>
              <div style={{ fontWeight: "bold" }}>مدیریت دستگاه‌ها</div>
              <span style={{ fontSize: "12px", color: "#ccc" }}>
                مشاهده و ویرایش دستگاه‌ها، مالک و تاریخ سرویس
              </span>
            </div>
          </div>

          <button
            onClick={() => navigate("/admin")}
            className={styles.backBtn}
            type="button"
          >
            <FaArrowLeft /> بازگشت به مدیریت
          </button>
        </div>

        <div style={getTopBarStyle(isMobile)}>
          <div style={searchWrapperStyle}>
            <FaSearch style={searchIconInlineStyle} />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو با سریال، نام دستگاه، مالک یا موبایل..."
              style={searchInputStyle}
            />

            {search && (
              <button
                type="button"
                onClick={clearSearch}
                style={clearSearchButtonStyle}
                title="پاک کردن"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => loadDevices(search)}
            style={refreshButtonStyle}
            disabled={loading}
          >
            <FaSyncAlt />
            {loading ? "در حال دریافت..." : "بروزرسانی"}
          </button>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <div style={getListCardStyle(isMobile)}>
          <div style={listHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FaMicrochip color="#f1c40f" />
              <strong>لیست دستگاه‌ها</strong>
            </div>

            <span style={{ color: "#bdc3c7", fontSize: "12px" }}>
              {loading ? "در حال بارگذاری..." : `${devices.length} دستگاه`}
            </span>
          </div>

          {isMobile ? (
            <div style={mobileCardsListStyle}>
              {devices.length === 0 && !loading ? (
                <div style={emptyTableStyle}>
                  دستگاهی برای نمایش وجود ندارد.
                </div>
              ) : (
                devices.map(renderDeviceMobileCard)
              )}
            </div>
          ) : (
            <div style={tableWrapperStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>دستگاه</th>
                    <th style={thStyle}>شماره سرویس</th>
                    <th style={thStyle}>سریال</th>
                    <th style={thStyle}>مالک</th>
                    <th style={thStyle}>موبایل مالک</th>
                    <th style={thStyle}>شروع</th>
                    <th style={thStyle}>پایان</th>
                    <th style={thStyle}>عملیات</th>
                  </tr>
                </thead>

                <tbody>
                  {devices.length === 0 && !loading ? (
                    <tr>
                      <td colSpan="8" style={emptyTableStyle}>
                        دستگاهی برای نمایش وجود ندارد.
                      </td>
                    </tr>
                  ) : (
                    devices.map((device) => (
                      <tr key={device.id}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: "bold" }}>
                            {device.device_name || "بدون نام"}
                          </div>
                        </td>

                        <td style={tdStyle}>
                          <code style={codeStyle}>
                            {device.service_number || "-"}
                          </code>
                        </td>

                        <td style={tdStyle}>
                          <code style={codeStyle}>{device.device_serial}</code>
                        </td>

                        <td style={tdStyle}>
                          <div style={{ fontWeight: "bold" }}>
                            {buildOwnerName(device) || "بدون نام"}
                          </div>
                          <div
                            style={{
                              color: "#95a5a6",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {device.owner_username}
                          </div>
                        </td>

                        <td style={tdStyle}>{device.owner_phone || "-"}</td>

                        <td style={tdStyle}>{formatDate(device.start_date)}</td>

                        <td style={tdStyle}>{formatDate(device.due_date)}</td>

                        <td style={tdStyle}>
                          <button
                            type="button"
                            onClick={() => openEditModal(device)}
                            style={editButtonStyle}
                          >
                            <FaEdit />
                            ویرایش
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editDevice && (
        <div style={modalOverlayStyle}>
          <form onSubmit={submitEditDevice} style={getModalStyle(isMobile)}>
            <div style={modalHeaderStyle}>
              <strong>ویرایش دستگاه</strong>

              <button
                type="button"
                onClick={closeEditModal}
                style={smallCloseButtonStyle}
              >
                <FaTimes />
              </button>
            </div>

            <div style={getModalBodyStyle(isMobile)}>
              <EditInput
                label="سریال دستگاه"
                value={editForm.device_serial}
                onChange={(v) =>
                  setEditForm((prev) => ({ ...prev, device_serial: v }))
                }
              />

              <EditInput
                label="نام دستگاه"
                value={editForm.device_name}
                onChange={(v) =>
                  setEditForm((prev) => ({ ...prev, device_name: v }))
                }
              />

              <EditInput
                label="شماره سرویس"
                value={editForm.service_number}
                onChange={(v) =>
                  setEditForm((prev) => ({ ...prev, service_number: v }))
                }
              />

              <div style={ownerDropdownWrapperStyle} ref={ownerDropdownRef}>
                <label style={modalLabelStyle}>مالک دستگاه</label>

                <div style={ownerSearchInputWrapperStyle}>
                  <input
                    value={ownerSearch}
                    onFocus={handleOwnerInputFocus}
                    onClick={handleOwnerInputFocus}
                    onChange={(e) => {
                      setOwnerSearch(e.target.value);
                      setEditForm((prev) => ({
                        ...prev,
                        owner_id: "",
                        owner_label: "",
                      }));
                      setIsOwnerDropdownOpen(true);
                    }}
                    placeholder="جستجوی مالک..."
                    style={{
                      ...modalInputStyle,
                      paddingLeft: editForm.owner_id ? "38px" : "12px",
                    }}
                  />

                  {editForm.owner_id && (
                    <button
                      type="button"
                      style={clearOwnerButtonStyle}
                      onClick={clearOwner}
                      title="حذف انتخاب"
                    >
                      <FaTimes />
                    </button>
                  )}

                  {!editForm.owner_id && (
                    <div style={ownerSearchIconStyle}>
                      {ownerUsersLoading ? "..." : <FaSearch />}
                    </div>
                  )}
                </div>

                {isOwnerDropdownOpen && (
                  <div style={ownerDropdownStyle}>
                    <div style={dropdownHeaderStyle}>
                      <span>
                        {ownerUsersLoading
                          ? "در حال دریافت کاربران..."
                          : `پیشنهاد کاربران (${ownerUsers.length})`}
                      </span>

                      <button
                        type="button"
                        style={dropdownCloseButtonStyle}
                        onClick={() => setIsOwnerDropdownOpen(false)}
                      >
                        <FaTimes />
                      </button>
                    </div>

                    <div style={dropdownListStyle}>
                      {ownerUsers.length === 0 && !ownerUsersLoading ? (
                        <div style={emptyUsersStyle}>
                          کاربری برای نمایش وجود ندارد.
                        </div>
                      ) : (
                        ownerUsers.map((user) => (
                          <div
                            key={user.id}
                            style={ownerItemStyle}
                            onClick={() => handleSelectOwner(user)}
                          >
                            <div>
                              <strong>
                                {user.name || ""} {user.family || ""}
                              </strong>
                              <div
                                style={{
                                  color: "#95a5a6",
                                  fontSize: "12px",
                                  marginTop: "4px",
                                }}
                              >
                                {user.username} | {user.phone_num1}
                              </div>
                            </div>

                            <div style={deviceCountBadgeStyle}>
                              {user.devices_count || 0} دستگاه
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <JalaliDateInput
                label="تاریخ شروع سرویس"
                value={editForm.start_date}
                isMobile={isMobile}
                inputStyleOverride={modalInputStyle}
                labelStyleOverride={modalLabelStyle}
                onChange={(v) =>
                  setEditForm((prev) => ({ ...prev, start_date: v }))
                }
              />

              <JalaliDateInput
                label="تاریخ پایان سرویس"
                value={editForm.due_date}
                isMobile={isMobile}
                inputStyleOverride={modalInputStyle}
                labelStyleOverride={modalLabelStyle}
                onChange={(v) =>
                  setEditForm((prev) => ({ ...prev, due_date: v }))
                }
              />
            </div>

            <button
              type="submit"
              disabled={editLoading}
              style={saveEditButtonStyle}
            >
              <FaSave />
              {editLoading ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const jalaliMonthNames = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const jalaliWeekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

const toPersianDigits = (value) =>
  String(value ?? "").replace(/\d/g, (d) => persianDigits[Number(d)]);
const pad2 = (value) => String(value).padStart(2, "0");
const div = (a, b) => Math.floor(a / b);

const gregorianToJalaliParts = (gy, gm, gd) => {
  const gDaysInMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  let jy;
  if (gy > 1600) {
    jy = 979;
    gy -= 1600;
  } else {
    jy = 0;
    gy -= 621;
  }
  const gy2 = gm > 2 ? gy + 1 : gy;
  let days =
    365 * gy +
    div(gy2 + 3, 4) -
    div(gy2 + 99, 100) +
    div(gy2 + 399, 400) -
    80 +
    gd +
    gDaysInMonth[gm - 1];
  jy += 33 * div(days, 12053);
  days %= 12053;
  jy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    jy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  const jm = days < 186 ? 1 + div(days, 31) : 7 + div(days - 186, 30);
  const jd = 1 + (days < 186 ? days % 31 : (days - 186) % 30);
  return { jy, jm, jd };
};

const jalaliToGregorianParts = (jy, jm, jd) => {
  let gy;
  if (jy > 979) {
    gy = 1600;
    jy -= 979;
  } else {
    gy = 621;
  }
  let days =
    365 * jy +
    div(jy, 33) * 8 +
    div((jy % 33) + 3, 4) +
    78 +
    jd +
    (jm < 7 ? (jm - 1) * 31 : (jm - 7) * 30 + 186);
  gy += 400 * div(days, 146097);
  days %= 146097;
  if (days > 36524) {
    gy += 100 * div(--days, 36524);
    days %= 36524;
    if (days >= 365) days += 1;
  }
  gy += 4 * div(days, 1461);
  days %= 1461;
  if (days > 365) {
    gy += div(days - 1, 365);
    days = (days - 1) % 365;
  }
  let gd = days + 1;
  const leap = (gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0;
  const gMonthDays = [
    0,
    31,
    leap ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  let gm = 1;
  while (gm <= 12 && gd > gMonthDays[gm]) {
    gd -= gMonthDays[gm];
    gm += 1;
  }
  return { gy, gm, gd };
};

const parseGregorianDate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [gy, gm, gd] = value.split("-").map(Number);
  const date = new Date(gy, gm - 1, gd);
  if (
    date.getFullYear() !== gy ||
    date.getMonth() !== gm - 1 ||
    date.getDate() !== gd
  )
    return null;
  return { gy, gm, gd };
};

const formatJalaliDate = (gregorianDate) => {
  const parsed = parseGregorianDate(gregorianDate);
  if (!parsed) return "";
  const { jy, jm, jd } = gregorianToJalaliParts(
    parsed.gy,
    parsed.gm,
    parsed.gd,
  );
  return toPersianDigits(`${jy}/${pad2(jm)}/${pad2(jd)}`);
};

const toGregorianDateString = (jy, jm, jd) => {
  const { gy, gm, gd } = jalaliToGregorianParts(jy, jm, jd);
  return `${gy}-${pad2(gm)}-${pad2(gd)}`;
};

const getTodayGregorianDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

const getJalaliMonthLength = (jy, jm) => {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  const thisYearStart = jalaliToGregorianParts(jy, 1, 1);
  const nextYearStart = jalaliToGregorianParts(jy + 1, 1, 1);
  const start = new Date(
    thisYearStart.gy,
    thisYearStart.gm - 1,
    thisYearStart.gd,
  );
  const next = new Date(
    nextYearStart.gy,
    nextYearStart.gm - 1,
    nextYearStart.gd,
  );
  return Math.round((next - start) / 86400000) === 366 ? 30 : 29;
};

const getJalaliFirstWeekday = (jy, jm) => {
  const { gy, gm, gd } = jalaliToGregorianParts(jy, jm, 1);
  return (new Date(gy, gm - 1, gd).getDay() + 1) % 7;
};

const useIsMobile = (breakpoint = 720) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

const JalaliDateInput = ({
  label,
  value,
  onChange,
  placeholder = "انتخاب تاریخ",
  isMobile = false,
  inputStyleOverride = modalInputStyle,
  labelStyleOverride = modalLabelStyle,
}) => {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedParts = parseGregorianDate(value);
  const selectedJalali = selectedParts
    ? gregorianToJalaliParts(
        selectedParts.gy,
        selectedParts.gm,
        selectedParts.gd,
      )
    : null;
  const todayParts = parseGregorianDate(getTodayGregorianDate());
  const todayJalali = todayParts
    ? gregorianToJalaliParts(todayParts.gy, todayParts.gm, todayParts.gd)
    : null;
  const [view, setView] = useState(
    () => selectedJalali || todayJalali || { jy: 1405, jm: 1, jd: 1 },
  );

  useEffect(() => {
    if (selectedJalali)
      setView({ jy: selectedJalali.jy, jm: selectedJalali.jm, jd: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const moveMonth = (offset) => {
    setView((prev) => {
      let nextYear = prev.jy;
      let nextMonth = prev.jm + offset;
      while (nextMonth < 1) {
        nextMonth += 12;
        nextYear -= 1;
      }
      while (nextMonth > 12) {
        nextMonth -= 12;
        nextYear += 1;
      }
      return { jy: nextYear, jm: nextMonth, jd: 1 };
    });
  };

  const selectDay = (day) => {
    onChange(toGregorianDateString(view.jy, view.jm, day));
    setIsOpen(false);
  };

  const days = [];
  for (let i = 0; i < getJalaliFirstWeekday(view.jy, view.jm); i += 1)
    days.push(null);
  for (let day = 1; day <= getJalaliMonthLength(view.jy, view.jm); day += 1)
    days.push(day);

  return (
    <div style={dateInputWrapperStyle} ref={wrapperRef}>
      <label style={labelStyleOverride}>{label}</label>
      <button
        type="button"
        style={{
          ...inputStyleOverride,
          ...dateButtonStyle,
          borderColor: value
            ? "rgba(46,204,113,0.42)"
            : inputStyleOverride.border,
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span>{value ? formatJalaliDate(value) : placeholder}</span>
        <FaCalendarAlt style={{ color: "#f1c40f", flex: "0 0 auto" }} />
      </button>
      {value && <div style={gregorianDateHintStyle}>ذخیره میلادی: {value}</div>}
      {isOpen && (
        <div style={getDatePickerStyle(isMobile)}>
          <div style={datePickerHeaderStyle}>
            <button
              type="button"
              style={dateNavButtonStyle}
              onClick={() => moveMonth(-1)}
            >
              ‹
            </button>
            <strong>
              {jalaliMonthNames[view.jm - 1]} {toPersianDigits(view.jy)}
            </strong>
            <button
              type="button"
              style={dateNavButtonStyle}
              onClick={() => moveMonth(1)}
            >
              ›
            </button>
          </div>
          <div style={dateWeekGridStyle}>
            {jalaliWeekDays.map((day) => (
              <span key={day} style={dateWeekDayStyle}>
                {day}
              </span>
            ))}
          </div>
          <div style={dateDaysGridStyle}>
            {days.map((day, idx) => {
              const selected = Boolean(
                selectedJalali &&
                selectedJalali.jy === view.jy &&
                selectedJalali.jm === view.jm &&
                selectedJalali.jd === day,
              );
              const isToday = Boolean(
                todayJalali &&
                todayJalali.jy === view.jy &&
                todayJalali.jm === view.jm &&
                todayJalali.jd === day,
              );
              return day ? (
                <button
                  key={`${view.jy}-${view.jm}-${day}`}
                  type="button"
                  style={{
                    ...dateDayButtonStyle,
                    ...(selected ? selectedDateDayStyle : {}),
                    ...(isToday && !selected ? todayDateDayStyle : {}),
                  }}
                  onClick={() => selectDay(day)}
                >
                  {toPersianDigits(day)}
                </button>
              ) : (
                <span key={`empty-${idx}`} />
              );
            })}
          </div>
          <div style={datePickerFooterStyle}>
            <button
              type="button"
              style={dateFooterButtonStyle}
              onClick={() => {
                onChange(getTodayGregorianDate());
                setIsOpen(false);
              }}
            >
              امروز
            </button>
            <button
              type="button"
              style={{
                ...dateFooterButtonStyle,
                background: "rgba(231,76,60,0.16)",
                color: "#ffb3b3",
              }}
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
            >
              پاک کردن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EditInput = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label style={modalLabelStyle}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={modalInputStyle}
    />
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={mobileInfoRowStyle}>
    <span style={{ color: "#95a5a6" }}>{label}:</span>
    <strong>{value || "-"}</strong>
  </div>
);

const getListCardStyle = (isMobile) => ({
  ...listCardStyle,
  ...(isMobile ? { padding: "12px", borderRadius: "14px" } : {}),
});

const mobileCardsListStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "12px",
};

const mobileDeviceCardStyle = {
  background: "rgba(16, 24, 32, 0.72)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "14px",
  padding: "13px",
  boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
};

const mobileDeviceCardHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "10px",
  marginBottom: "10px",
};

const mobileCardTitleStyle = {
  color: "#fff",
  fontWeight: "bold",
  marginBottom: "6px",
};

const mobileServiceCodeStyle = {
  background: "#101820",
  color: "#f1c40f",
  borderRadius: "6px",
  padding: "4px 7px",
  direction: "ltr",
  display: "inline-block",
  fontSize: "12px",
};

const mobileMetaGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "8px",
};

const mobileInfoRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "10px",
  background: "#101820",
  borderRadius: "8px",
  padding: "9px 10px",
  fontSize: "12px",
};

const getMobileEditButtonStyle = (baseStyle) => ({
  ...baseStyle,
  justifyContent: "center",
  padding: "9px 10px",
  whiteSpace: "nowrap",
});

const buildOwnerName = (device) => {
  return `${device.owner_name || ""} ${device.owner_family || ""}`.trim();
};

const buildOwnerLabel = (device) => {
  const name = buildOwnerName(device);
  const phone = device.owner_phone || device.owner_username || "";
  return `${name} - ${phone}`.trim();
};

const formatDate = (date) => {
  return formatJalaliDate(date) || "-";
};

const topBarStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: "12px",
  marginTop: "24px",
  direction: "rtl",
};

const searchWrapperStyle = {
  position: "relative",
};

const searchInputStyle = {
  width: "100%",
  background: "rgba(16, 24, 32, 0.92)",
  color: "#fff",
  border: "1px solid rgba(52, 152, 219, 0.35)",
  borderRadius: "12px",
  padding: "12px 40px 12px 42px",
  outline: "none",
  boxSizing: "border-box",
};

const searchIconInlineStyle = {
  position: "absolute",
  right: "13px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#4dabf7",
};

const clearSearchButtonStyle = {
  position: "absolute",
  left: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  border: "none",
  background: "rgba(231,76,60,0.18)",
  color: "#ffb3b3",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const refreshButtonStyle = {
  background: "#3498db",
  color: "#fff",
  border: "none",
  borderRadius: "12px",
  padding: "0 18px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontWeight: "bold",
  minHeight: "44px",
};

const errorStyle = {
  background: "rgba(231, 76, 60, 0.15)",
  border: "1px solid rgba(231, 76, 60, 0.4)",
  color: "#ffb3b3",
  borderRadius: "10px",
  padding: "11px",
  marginTop: "14px",
  fontSize: "13px",
  direction: "rtl",
};

const listCardStyle = {
  background: "rgba(31, 45, 58, 0.92)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  padding: "16px",
  color: "#fff",
  marginTop: "18px",
  boxShadow: "0 10px 35px rgba(0,0,0,0.25)",
  direction: "rtl",
};

const listHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "14px",
};

const tableWrapperStyle = {
  overflowX: "auto",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "1080px",
  fontSize: "13px",
};

const thStyle = {
  textAlign: "right",
  color: "#f1c40f",
  background: "#101820",
  padding: "12px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.12)",
  position: "sticky",
  top: 0,
};

const tdStyle = {
  padding: "11px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  color: "#ecf0f1",
  verticalAlign: "middle",
};

const emptyTableStyle = {
  padding: "30px",
  textAlign: "center",
  color: "#95a5a6",
};

const codeStyle = {
  background: "#101820",
  color: "#f1c40f",
  borderRadius: "6px",
  padding: "4px 7px",
  direction: "ltr",
  display: "inline-block",
};

const editButtonStyle = {
  background: "#3498db",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "8px 10px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
};

const modalStyle = {
  width: "min(760px, 95vw)",
  background: "#1f2d3a",
  color: "#fff",
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.1)",
  boxShadow: "0 10px 40px rgba(0,0,0,0.45)",
  padding: "0",
  direction: "rtl",
  overflow: "visible",
};

const modalHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#17212b",
  padding: "14px 16px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  color: "#f1c40f",
  borderTopLeftRadius: "16px",
  borderTopRightRadius: "16px",
};

const modalBodyStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(220px, 1fr))",
  gap: "14px",
  padding: "16px",
};

const modalLabelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "13px",
  color: "#bdc3c7",
};

const modalInputStyle = {
  width: "100%",
  background: "#101820",
  color: "#fff",
  border: "1px solid #34495e",
  borderRadius: "8px",
  padding: "10px",
  outline: "none",
  boxSizing: "border-box",
};

const smallCloseButtonStyle = {
  border: "none",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  width: "28px",
  height: "28px",
  borderRadius: "50%",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const saveEditButtonStyle = {
  margin: "0 16px 16px",
  width: "calc(100% - 32px)",
  background: "#2ecc71",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "12px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontWeight: "bold",
};

const ownerDropdownWrapperStyle = {
  position: "relative",
};

const ownerSearchInputWrapperStyle = {
  position: "relative",
};

const clearOwnerButtonStyle = {
  position: "absolute",
  left: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  width: "26px",
  height: "26px",
  borderRadius: "50%",
  border: "none",
  background: "rgba(231, 76, 60, 0.18)",
  color: "#ffb3b3",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const ownerSearchIconStyle = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#95a5a6",
  fontSize: "13px",
};

const ownerDropdownStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  left: 0,
  zIndex: 100000,
  background: "#101820",
  border: "1px solid rgba(52, 152, 219, 0.35)",
  borderRadius: "12px",
  boxShadow: "0 14px 40px rgba(0,0,0,0.45)",
  overflow: "hidden",
};

const dropdownHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "9px 11px",
  background: "#17212b",
  color: "#bdc3c7",
  fontSize: "12px",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const dropdownCloseButtonStyle = {
  width: "24px",
  height: "24px",
  border: "none",
  borderRadius: "50%",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const dropdownListStyle = {
  maxHeight: "260px",
  overflowY: "auto",
  padding: "8px",
  display: "grid",
  gap: "8px",
};

const ownerItemStyle = {
  background: "#17212b",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "10px",
  padding: "11px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
};

const emptyUsersStyle = {
  background: "#17212b",
  borderRadius: "10px",
  padding: "16px",
  color: "#95a5a6",
  fontSize: "13px",
  border: "1px dashed rgba(255,255,255,0.12)",
};

const deviceCountBadgeStyle = {
  background: "rgba(52,152,219,0.14)",
  color: "#4dabf7",
  border: "1px solid rgba(52,152,219,0.28)",
  borderRadius: "999px",
  padding: "4px 9px",
  fontSize: "11px",
  whiteSpace: "nowrap",
};

const dateInputWrapperStyle = { position: "relative" };
const dateButtonStyle = {
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  textAlign: "right",
  minHeight: "42px",
};
const gregorianDateHintStyle = {
  marginTop: "5px",
  color: "#7f8c8d",
  fontSize: "11px",
  direction: "ltr",
  textAlign: "left",
};
const getDatePickerStyle = (isMobile) => ({
  position: isMobile ? "fixed" : "absolute",
  top: isMobile ? "auto" : "calc(100% + 8px)",
  right: isMobile ? "14px" : 0,
  left: isMobile ? "14px" : "auto",
  bottom: isMobile ? "14px" : "auto",
  width: isMobile ? "auto" : "320px",
  maxHeight: isMobile ? "78vh" : "none",
  overflowY: "auto",
  background: "#101820",
  color: "#fff",
  border: "1px solid rgba(52,152,219,0.42)",
  borderRadius: "16px",
  padding: "12px",
  boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
  zIndex: 100000,
  direction: "rtl",
});
const datePickerHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "10px",
  color: "#f1c40f",
};
const dateNavButtonStyle = {
  width: "34px",
  height: "34px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.1)",
  background: "#17212b",
  color: "#fff",
  cursor: "pointer",
  fontSize: "22px",
  lineHeight: 1,
};
const dateWeekGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "5px",
  marginBottom: "6px",
};
const dateWeekDayStyle = {
  textAlign: "center",
  color: "#95a5a6",
  fontSize: "12px",
};
const dateDaysGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "5px",
};
const dateDayButtonStyle = {
  height: "36px",
  borderRadius: "10px",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "#17212b",
  color: "#ecf0f1",
  cursor: "pointer",
  fontWeight: "bold",
};
const selectedDateDayStyle = {
  background: "rgba(46,204,113,0.28)",
  border: "1px solid rgba(46,204,113,0.65)",
  color: "#2ecc71",
};
const todayDateDayStyle = {
  border: "1px solid rgba(241,196,15,0.55)",
  color: "#f1c40f",
};
const datePickerFooterStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "8px",
  marginTop: "10px",
};
const dateFooterButtonStyle = {
  border: "none",
  borderRadius: "10px",
  padding: "9px 10px",
  background: "rgba(52,152,219,0.18)",
  color: "#4dabf7",
  cursor: "pointer",
  fontWeight: "bold",
};

const getTopBarStyle = (isMobile) => ({
  ...topBarStyle,
  ...(isMobile ? { gridTemplateColumns: "1fr", gap: "10px" } : {}),
});
const getModalStyle = (isMobile) => ({
  ...modalStyle,
  ...(isMobile
    ? {
        width: "calc(100vw - 20px)",
        maxHeight: "92vh",
        overflowY: "auto",
        borderRadius: "14px",
      }
    : {}),
});
const getModalBodyStyle = (isMobile) => ({
  ...modalBodyStyle,
  ...(isMobile
    ? { gridTemplateColumns: "1fr", gap: "12px", padding: "12px" }
    : {}),
});

export default AdminDevices;
